import type { TranslationMap } from "../lib/types.ts";

export const en: TranslationMap = {
  common: {
    health: "Health",
    ok: "OK",
    offline: "Offline",
    connect: "Connect",
    refresh: "Refresh",
    retry: "Retry",
    cancel: "Cancel",
    close: "Close",
    yes: "Yes",
    no: "No",
    prev: "Prev",
    next: "Next",
    errorTitle: "Request failed",
    enabled: "Enabled",
    disabled: "Disabled",
    na: "n/a",
    docs: "Docs",
    resources: "Resources",
    loading: "Loading…",
    save: "Save",
    edit: "Edit",
  },
  nav: {
    chat: "Chat",
    control: "Control",
    agent: "Agent",
    settings: "Settings",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
  },
  tabs: {
    agents: "Agents",
    overview: "Overview",
    channels: "Business Knowledge",
    instances: "Out of Stock",
    sessions: "Procurement",
    work: "Quotation",
    cron: "Order Fulfill",
    skills: "Skills",
    reports: "Reports",
    nodes: "Nodes",
    chat: "Chat",
    config: "Config",
    debug: "Debug",
    logs: "Logs",
    "admin-data": "Data",
  },
  subtitles: {
    agents: "Manage agent workspaces, tools, and identities.",
    overview: "Gateway status, entry points, and a fast health read.",
    channels: "Edit wanding_business_knowledge.md for selection and matching.",
    instances: "OOS dashboard: stats and product list without asking the agent.",
    sessions: "Procurement suggestions from shortage; approve to save and notify buyer.",
    work: "Batch quotation: upload files, identify, match price and stock, fill and save.",
    cron: "Pending quotation drafts; confirm to create order and lock stock.",
    skills: "Manage skill availability and API key injection.",
    reports: "Weekly sales invoice reports. Click a record to view full content.",
    nodes: "Paired devices, capabilities, and command exposure.",
    chat: "Direct gateway chat session for quick interventions.",
    config: "Edit ~/.openclaw/openclaw.json safely.",
    debug: "Gateway snapshots, events, and manual RPC calls.",
    logs: "Live tail of the gateway file logs.",
    "admin-data": "Wanding price library and product mapping (admin password).",
  },
  overview: {
    health: {
      title: "Health & stats",
      subtitle: "High-level view of instances, sessions, and cron.",
      lastErrorLabel: "Last error",
      noError: "No recent errors.",
    },
    access: {
      title: "Gateway Access",
      subtitle: "Where the dashboard connects and how it authenticates.",
      wsUrl: "WebSocket URL",
      token: "Gateway Token",
      password: "Password (not stored)",
      sessionKey: "Default Session Key",
      language: "Language",
      connectHint: "Click Connect to apply connection changes.",
      trustedProxy: "Authenticated via trusted proxy.",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Latest gateway handshake information.",
      status: "Status",
      uptime: "Uptime",
      tickInterval: "Tick Interval",
      lastChannelsRefresh: "Last Channels Refresh",
      channelsHint: "Use Channels to link WhatsApp, Telegram, Discord, Signal, or iMessage.",
    },
    stats: {
      instances: "Instances",
      instancesHint: "Presence beacons in the last 5 minutes.",
      sessions: "Sessions",
      sessionsHint: "Recent session keys tracked by the gateway.",
      cron: "Cron",
      cronNext: "Next wake {time}",
    },
    notes: {
      title: "Notes",
      subtitle: "Quick reminders for remote control setups.",
      tailscaleTitle: "Tailscale serve",
      tailscaleText: "Prefer serve mode to keep the gateway on loopback with tailnet auth.",
      sessionTitle: "Session hygiene",
      sessionText: "Use /new or sessions.patch to reset context.",
      cronTitle: "Cron reminders",
      cronText: "Use isolated sessions for recurring runs.",
    },
    auth: {
      required: "This gateway requires auth. Add a token or password, then click Connect.",
      failed:
        "Auth failed. Re-copy a tokenized URL with {command}, or update the token, then click Connect.",
    },
    insecure: {
      hint: "This page is HTTP, so the browser blocks device identity. Use HTTPS (Tailscale Serve) or open {url} on the gateway host.",
      stayHttp: "If you must stay on HTTP, set {config} (token-only).",
    },
    oos: {
      title: "Out-of-stock overview",
      subtitle: "Recent out-of-stock stats; see Instances tab for full details.",
      stats: {
        totalRecords: "Total records",
        outOfStockCount: "Out-of-stock items",
        today: "Added today",
        reportedGe2: "Reported out-of-stock ≥2 times",
      },
      empty: "No stats yet; check back later on the Instances tab.",
    },
    shortage: {
      title: "Shortage overview",
      subtitle: "Shortage stats after Work matching; focus on critical items.",
      stats: {
        totalRecords: "Total records",
        shortageProductCount: "Shortage items",
        today: "Added today",
        reportedGe2: "Reported shortage ≥2 times",
      },
      empty: "No stats yet; check back later on the Instances tab.",
    },
    dashboard: {
      kpi: {
        oosProducts: "Out-of-stock products",
        shortageProducts: "Shortage products",
        pendingQuotations: "Pending quotations",
        todayNewQuotations: "New quotations today",
        shortageQuotations: "Shortage quotations",
        replenishmentDrafts: "Replenishment drafts",
      },
      chart: {
        quotationTrend: "Quotation trend (last 7 days)",
        stockTrend: "Out-of-stock / shortage trend (last 7 days)",
        quotationSeries: "Quotations",
        oosSeries: "Out-of-stock",
        shortageSeries: "Shortage",
        loading: "Loading...",
        empty: "No data",
      },
      error: "Dashboard data load failed: {error}",
    },
  },
  chat: {
    disconnected: "Disconnected from gateway.",
    refreshTitle: "Refresh chat data",
    thinkingToggle: "Toggle assistant thinking/working output",
    focusToggle: "Toggle focus mode (hide sidebar + page header)",
    onboardingDisabled: "Disabled during onboarding",
    ui: {
      compaction: {
        active: "Compacting context…",
        done: "Context compacted",
        divider: "Compaction",
      },
      attachments: {
        previewAlt: "Attachment preview",
        remove: "Remove attachment",
      },
      upload: {
        label: "Upload Excel or PDF",
        button: "Upload Excel/PDF",
        remove: "Remove uploaded file",
      },
      queue: {
        title: "Queued ({count})",
        imageItem: "Image ({count})",
        remove: "Remove queued message",
      },
      candidatesPreview: {
        query: "Searching: {keywords}",
        colCode: "Code",
        colName: "Product",
        colPrice: "Unit price",
        more: "…and {n} more",
        selecting: "AI is choosing the best match from {n} candidate(s)…",
      },
      compose: {
        placeholder: {
          withImages: "Add a message or paste more images…",
          default:
            "Message (↩ to send, Shift+↩ for line breaks; paste images or upload/drag Excel/PDF)",
          disconnected: "Connect to the gateway to start chatting…",
        },
        newMessages: "New messages",
        dropHint: "Drop to upload Excel/PDF",
        label: "Message",
        stop: "Stop",
        newSession: "New session",
        send: "Send",
        queue: "Queue",
        exitFocus: "Exit focus mode",
      },
    },
  },
  work: {
    runHint: "Please select at least one file before running.",
    saveConfirm: "Confirm save quotation draft and persist to database?",
    saveSuccessHint: "Saved. You can confirm it on the Order Fulfill page.",
    stageExtract: "Extract sheet data",
    stageMatch: "Match price & inventory",
    stageFill: "Fill quotation",
    uploadTitle: "Quotation files (multiple)",
    removeFile: "Remove",
    noFiles: "No files uploaded (.xlsx/.xls/.xlsm).",
    customerLevel: "Customer level",
    registerOos: "Register out-of-stock items",
    currentStage: "Current stage",
    running: "Running",
    run: "Run",
    cancel: "Cancel",
    statusLabel: "Status",
    awaitingTitle: "Need your choices",
    awaitingHint: "Select one option for each ambiguous item, then continue.",
    qty: "Qty",
    choiceSelect: "Candidate selection",
    choiceOos: "Mark as out of stock",
    resuming: "Resuming",
    resume: "Confirm and continue",
    savedDraftNo: "Quotation draft saved: {no}",
    pendingDraftTitle: "Pending quotation draft",
    pendingDraftHint: "Review and edit before saving to database.",
    saving: "Saving...",
    saveDraft: "Confirm and save",
    resultTitle: "Execution result",
    download: "Download {name}",
    trace: "Trace ({count})",
    traceTimelineTitle: "Execution steps",
    traceRawDebug: "Raw trace (debug)",
    traceStep: "Step {n}",
    traceTypeToolCall: "Call",
    traceTypeObservation: "Result",
    traceTypeMetrics: "Timing",
    traceToolExtract: "Extract quotation rows",
    traceToolMatch: "Match price & stock",
    traceToolFill: "Fill Excel",
    traceToolShortageReport: "Shortage report",
    traceToolFallback: "{name}",
    traceParseError: "Could not parse as JSON; raw content:",
    traceFieldSuccess: "Success",
    traceFieldRows: "Rows",
    traceFieldFilled: "Filled rows",
    traceFieldOutput: "Output file",
    traceFieldSummary: "Summary",
    traceFieldError: "Error",
    lineProduct: "Requested item name",
    lineSpec: "Requested spec",
    lineQty: "Qty",
    lineCode: "Product number",
    lineQuoteName: "Quoted item name",
    lineQuoteSpec: "Quoted spec",
    linePrice: "Unit price",
    lineAmount: "Total",
    lineAvailable: "Available",
    lineShortfall: "Shortfall",
    lineIsShortage: "Shortage",
    textInputTitle: "Text input (quotation)",
    textInputHint: "Enter product list (multi-line or semicolon/comma separated); generated file will run with uploaded files.",
    textInputPlaceholder: "e.g. Cable 3*2.5 100m; Switch 20 pcs",
    generateFromText: "Generate from text",
    textGenerating: "Generating…",
    priceLevels: {
      FACTORY_INC_TAX: "Factory price (incl. tax)",
      FACTORY_EXC_TAX: "Factory price (excl. tax)",
      PURCHASE_EXC_TAX: "Purchase price (excl. tax)",
      A_MARGIN: "Tier A (2nd-level agent) · margin",
      A_QUOTE: "Tier A (2nd-level agent) · quotation price",
      B_MARGIN: "Tier B (1st-level agent) · margin",
      B_QUOTE: "Tier B (1st-level agent) · quotation price",
      C_MARGIN: "Tier C (Juwan key account) · margin",
      C_QUOTE: "Tier C (Juwan key account) · quotation price",
      D_MARGIN: "Tier D (Qingshan key account) · margin",
      D_QUOTE: "Tier D (Qingshan key account) · quotation price",
      D_LOW: "Tier D (Qingshan key account) · reduced margin",
      E_MARGIN: "Tier E (Datang key account, freight included) · margin",
      E_QUOTE: "Tier E (Datang key account, freight included) · quotation price",
    },
    fileDisplayName: "Quotation file display name",
    status: {
      idle: "Idle",
      running: "Running",
      awaitingChoices: "Awaiting choices",
      resuming: "Resuming",
      done: "Done",
      error: "Error",
    },
    fallbackDraftName: "Untitled quotation",
  },
  fulfill: {
    title: "Pending quotation drafts",
    subtitle: "Load persisted drafts and confirm to create formal orders.",
    loading: "Loading...",
    refreshList: "Refresh list",
    filterPlaceholder: "Search by draft no/name/source",
    sortBy: "Sort by",
    sortDir: "Order",
    sortCreatedAt: "Created time",
    sortDraftNo: "Draft no",
    sortName: "Name",
    sortDesc: "Newest first",
    sortAsc: "Oldest first",
    pageSize: "Page size",
    total: "Total: {total}",
    page: "Page {current}/{total}",
    listTitle: "List",
    listSubtitle: "View detail first, then confirm order.",
    colDraftNo: "Draft No",
    colName: "Name",
    colSource: "Source",
    colCreatedAt: "Created At",
    colActions: "Actions",
    viewDetail: "View",
    confirmAction: "Confirm order",
    confirming: "Confirming...",
    detailTitle: "Draft detail · {draftNo}",
    closeDetail: "Close detail",
    lineProduct: "Product",
    lineSpec: "Spec",
    lineQty: "Qty",
    lineCode: "Code",
    lineQuoteName: "Quote name",
    lineQuoteSpec: "Quoted spec",
    linePrice: "Unit price",
    lineAmount: "Amount",
    lineAvailable: "Available",
    lineShortfall: "Shortfall",
    lineIsShortage: "Shortage",
    noDrafts: "No pending quotation drafts.",
    confirmTitle: "Order confirmed",
    confirmPrompt: "Confirm order? {count} line(s), total amount {amount}.",
    confirmPromptSimple: "Confirm to convert this quotation into a formal order?",
    orderId: "Order ID",
  },
  procurement: {
    title: "Procurement suggestions",
    subtitle: "Generated from shortage records; approve to persist and notify buyers.",
    loading: "Loading...",
    refreshList: "Refresh list",
    batchApprove: "Batch approve",
    approving: "Approving...",
    approveSingle: "Approve",
    approveConfirm: "Confirm approval and notify buyer?",
    approveBatchConfirm: "Confirm approval of {count} item(s) and notify buyer?",
    noSuggestions: "No shortage products; no procurement suggestions.",
    noPending: "No pending items (approved items are hidden).",
    listHint: "Select multiple to batch approve; click Approve to save and notify buyer.",
    filterPlaceholder: "Search by product/spec/code/key",
    sortBy: "Sort by",
    sortDir: "Order",
    sortUploadedAt: "Reported time",
    sortShortfall: "Shortfall",
    sortCount: "Report count",
    sortProduct: "Product name",
    sortDesc: "High to low / newest",
    sortAsc: "Low to high / oldest",
    pageSize: "Page size",
    total: "Total: {total}",
    page: "Page {current}/{total}",
    listTitle: "Shortage item list",
    selectAll: "Select all filtered items",
    selectItem: "Select item",
    colProduct: "Product",
    colSpec: "Spec",
    colShortfall: "Shortfall",
    colCode: "Code",
    colCount: "Count",
    colActions: "Actions",
    approvedCount: "Approved {count} item(s).",
  },
  replenishment: {
    title: "Replenishment",
    subtitle: "Enter product name or code and quantity to generate a draft; view and confirm in the list below to run inventory supplement.",
    productOrCodePlaceholder: "Product name or code",
    quantityPlaceholder: "Quantity",
    generateDraft: "Generate replenishment draft",
    creating: "Creating…",
    addRow: "Add row",
    removeRow: "Remove",
    refreshList: "Refresh list",
    loading: "Loading…",
    listTitle: "Replenishment drafts",
    listHint: "Drafts are created via LLM and inventory tools; confirm to run inventory changes.",
    noDrafts: "No replenishment drafts.",
    colDraftNo: "Draft No",
    colName: "Name",
    colCreatedAt: "Created",
    colStatus: "Status",
    colActions: "Actions",
    viewDetail: "View",
    confirm: "Confirm replenishment",
    confirming: "Executing…",
    confirmPrompt: "Confirm and run all inventory changes for this draft?",
    delete: "Delete",
    deleteConfirm: "Delete this replenishment draft? This cannot be undone.",
    detailTitle: "Draft detail ({no})",
    detailSubtitle: "Products, current stock and replenishment quantities.",
    colCode: "Code",
    colProduct: "Product",
    colSpec: "Spec",
    colCurrentQty: "Current qty",
    colQuantity: "Quantity",
  },
  oos: {
    title: "Out-of-stock dashboard",
    subtitle: "Overview and list of out-of-stock products, without asking the agent.",
    actions: {
      loading: "Loading…",
      refresh: "Refresh",
      addManual: "Add manually",
      confirm: "Confirm",
      delete: "Delete",
      deleteHint: "Delete this out-of-stock item",
    },
    db: {
      local: "Using local database",
    },
    stats: {
      totalRecords: "Total records",
      outOfStockCount: "Out-of-stock items",
      today: "New today",
      reportedGe2: "Reported out-of-stock ≥2 times",
      emailSentProductCount: "Products with email sent",
    },
    empty: {
      stats: "No stats yet.",
      list: "No out-of-stock records.",
    },
    list: {
      title: "Out-of-stock product list",
      more: "Total {count} out-of-stock products; showing first 50 only",
      meta: "Qty: {qty} {unit} · Reported out-of-stock {count} time(s) · Email: {email}",
    },
    addForm: {
      title: "Add out-of-stock record",
      namePlaceholder: "Product name (required)",
      specPlaceholder: "Specification",
      qtyPlaceholder: "Quantity",
      unitPlaceholder: "Unit",
    },
    email: {
      sent: "Sent",
      notSent: "Not sent",
    },
    byFile: {
      title: "By file",
      empty: "None",
      count: "Records: {count}",
    },
    byTime: {
      title: "By time (last 30 days)",
      empty: "None",
      count: "New: {count}",
    },
  },
  shortage: {
    title: "Shortage records",
    subtitle:
      "Saved when Work detects insufficient stock; overview and list of shortage items.",
    actions: {
      loading: "Loading…",
      refresh: "Refresh",
      addManual: "Add manually",
      confirm: "Confirm",
      delete: "Delete",
      deleteHint: "Delete this shortage item",
    },
    db: {
      local: "Using local database",
    },
    stats: {
      totalRecords: "Total records",
      shortageProductCount: "Shortage products",
      today: "New today",
      reportedGe2: "Reported shortage ≥2 times",
    },
    empty: {
      stats: "No stats yet.",
      list: "No shortage records.",
    },
    list: {
      title: "Shortage product list",
      more: "Total {count} shortage products; showing first 50 only",
      meta: "Required: {qty} · Available: {avail} · Shortfall: {diff} · Reported shortage {count} time(s)",
    },
    addForm: {
      title:
        "Add shortage record (product name, spec, required, available; shortfall will be auto-calculated)",
      namePlaceholder: "Product name (required)",
      specPlaceholder: "Specification",
      qtyPlaceholder: "Required",
      availPlaceholder: "Available",
      qtyTitle: "Required quantity",
      availTitle: "Available quantity",
      diffTitle: "Shortfall = required − available; auto-calculated on submit",
      diffText: "Shortfall: auto-calculated",
    },
    byFile: {
      title: "By file",
      empty: "None",
      count: "Records: {count}",
    },
    byTime: {
      title: "By time (last 30 days)",
      empty: "None",
      count: "New: {count}",
    },
  },
  businessKnowledge: {
    title: "Business knowledge",
    subtitle:
      "Edit wanding_business_knowledge.md for selection and matching. The LLM will use the latest content after saving.",
    lastSavedAt: "Saved at {time}",
    actions: {
      reloading: "Loading…",
      reload: "Reload",
      saving: "Saving…",
      save: "Save",
    },
    relatedFiles: {
      title: "Related data files",
      hint: "Selection and historical quotations rely on these Excel files. Copy the path to open them in Explorer or Excel when updating.",
      mappingTableLabel: "Inquiry mapping table (historical quotations):",
      priceLibraryLabel: "Wanding price library:",
      copyPath: "Copy path",
    },
    editor: {
      placeholder: "[Business knowledge]\n1. …",
    },
  },
  agents: {
    reports: {
      whereHint:
        "Weekly reports live here: open Agents, pick an agent, then the Skills sub-tab (not the sidebar Skills catalog).",
      title: "Weekly reports",
      subtitle: "Scheduled tasks and recent runs for this agent.",
      tokenLabel: "Reports admin token",
      tasks: "Tasks",
      noTasks: "No report tasks configured.",
      enabled: "Enabled",
      cron: "Cron",
      timezone: "Timezone",
      run: "Run now",
      latestRecords: "Latest runs",
      noRecords: "No run history yet.",
      detailLoading: "Loading report...",
      detailEmpty: "Select a record on the left to view the report.",
      detailNoMd: "No report text is stored yet for this record.",
      copyBtn: "Copy",
      copiedBtn: "Copied!",
      reformatBtn: "Regenerate text",
      tabData: "Data Report",
      tabAnalysis: "AI Analysis",
      analysisLoading: "Generating analysis…",
      analysisPending: "Pending analysis, please wait…",
      analysisEmpty: "No analysis content yet.",
      analysisFailed: "Analysis generation failed.",
      analysisFailedHint: "Analysis failed — possibly due to token limits or backend errors.",
      troubleshooting: "Troubleshooting",
      stepCheckToken: "Check if API token has remaining quota",
      stepCheckBackendLog: "Check backend logs for errors",
      stepRetry: "Click Retry above",
      reanalyzeBtn: "Re-analyze",
      copy: "Copy",
      errorUnknown: "Unknown error",
      metricTotal: "Total Invoice Amount",
      metricCount: "Invoice Count",
      tableDaily: "Daily Trend",
      tableCustomers: "Top 10 Customers",
      tableStatus: "Status Summary",
      colDate: "Date",
      colCount: "Count",
      colAmount: "Amount",
      colCustomer: "Customer",
      colStatus: "Status",
      colRank: "Rank",
      filterSearch: "Search date/amount",
      filterAll: "All",
      filterDone: "Done",
      filterFailed: "Failed",
      filterPending: "Pending",
      filterRunning: "Running",
    },
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
  },
};
